import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';
import { useFocusVisible } from '../../hooks/useFocusVisible';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { motion } from '../../theme/motion';
import { tokens } from '../../theme/tokens';

interface Tab {
  key: string;
  label: string;
  icon?: ReactNode;
  count?: number;
}

interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onChange: (key: string) => void;
  variant: 'underline' | 'pills' | 'segment';
  style?: ViewStyle;
  testID?: string;
}

interface WebKeyEvent {
  key?: string;
  nativeEvent?: { key?: string };
  preventDefault?: () => void;
}

function minimumTargetSize() {
  return Platform.OS === 'android'
    ? tokens.interaction.minimumTarget.android
    : tokens.interaction.minimumTarget.default;
}

function useTabKeyboardNavigation(tabs: Tab[], onChange: (key: string) => void) {
  const refs = useRef<Array<View | null>>([]);

  const registerTab = (index: number) => (node: View | null) => {
    refs.current[index] = node;
  };

  const handleKeyDown = (event: WebKeyEvent, index: number) => {
    const key = event.key ?? event.nativeEvent?.key;
    let nextIndex: number | undefined;

    if (key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    if (key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (key === 'Home') nextIndex = 0;
    if (key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;

    const nextTab = tabs[nextIndex];
    if (!nextTab) return;

    event.preventDefault?.();
    onChange(nextTab.key);
    (refs.current[nextIndex] as unknown as { focus?: () => void } | null)?.focus?.();
  };

  return { handleKeyDown, registerTab };
}

function CountBadge({
  count,
  active,
}: Readonly<{
  count: number;
  active: boolean;
}>) {
  const badgeBg = active ? colors.primary.subtle : colors.background.secondary;
  const badgeText = active ? colors.primary[500] : colors.text.tertiary;

  return (
    <View style={[badgeStyles.badge, { backgroundColor: badgeBg }]}>
      <Text style={[badgeStyles.badgeText, { color: badgeText }]}>
        {count > 99 ? '99+' : count}
      </Text>
    </View>
  );
}

function UnderlineTabs({
  tabs,
  activeTab,
  onChange,
  style,
  testID,
}: Readonly<Omit<TabsProps, 'variant'>>) {
  const [focusedKey, setFocusedKey] = useState<string>();
  // Only a keyboard focus draws a ring — a click should just move the pill.
  const focusRingVisible = useFocusVisible();
  const keyboard = useTabKeyboardNavigation(tabs, onChange);
  const targetSize = minimumTargetSize();

  return (
    <View style={[underlineStyles.container, style]} testID={testID} accessibilityRole="tablist">
      {tabs.map((tab, index) => {
        const active = tab.key === activeTab;
        return (
          <Pressable
            key={tab.key}
            ref={keyboard.registerTab(index)}
            onPress={() => onChange(tab.key)}
            onBlur={() => setFocusedKey(undefined)}
            onFocus={() => setFocusedKey(tab.key)}
            {...(Platform.OS === 'web'
              ? ({
                  onKeyDown: (event: WebKeyEvent) => keyboard.handleKeyDown(event, index),
                  tabIndex: active ? 0 : -1,
                } as object)
              : {})}
            style={({ pressed }) => [
              underlineStyles.tab,
              { minHeight: targetSize, minWidth: targetSize },
              active && underlineStyles.tabActive,
              pressed && underlineStyles.tabPressed,
              focusedKey === tab.key && focusRingVisible && interactionStyles.focusVisible,
            ]}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            testID={testID ? `${testID}-tab-${tab.key}` : undefined}
          >
            <View style={underlineStyles.tabContent}>
              {tab.icon && <View style={underlineStyles.iconContainer}>{tab.icon}</View>}
              <Text
                style={[
                  underlineStyles.tabText,
                  active ? underlineStyles.tabTextActive : underlineStyles.tabTextInactive,
                ]}
              >
                {tab.label}
              </Text>
              {tab.count !== undefined && <CountBadge count={tab.count} active={active} />}
            </View>
            <View style={[underlineStyles.indicator, active && underlineStyles.indicatorActive]} />
          </Pressable>
        );
      })}
    </View>
  );
}

function PillsTabs({
  tabs,
  activeTab,
  onChange,
  style,
  testID,
}: Readonly<Omit<TabsProps, 'variant'>>) {
  const [focusedKey, setFocusedKey] = useState<string>();
  // Only a keyboard focus draws a ring — a click should just move the pill.
  const focusRingVisible = useFocusVisible();
  const keyboard = useTabKeyboardNavigation(tabs, onChange);
  const targetSize = minimumTargetSize();

  return (
    <View style={[pillsStyles.container, style]} testID={testID} accessibilityRole="tablist">
      {tabs.map((tab, index) => {
        const active = tab.key === activeTab;
        return (
          <Pressable
            key={tab.key}
            ref={keyboard.registerTab(index)}
            onPress={() => onChange(tab.key)}
            onBlur={() => setFocusedKey(undefined)}
            onFocus={() => setFocusedKey(tab.key)}
            {...(Platform.OS === 'web'
              ? ({
                  onKeyDown: (event: WebKeyEvent) => keyboard.handleKeyDown(event, index),
                  tabIndex: active ? 0 : -1,
                } as object)
              : {})}
            style={({ pressed }) => [
              pillsStyles.tab,
              { minHeight: targetSize, minWidth: targetSize },
              active ? pillsStyles.tabActive : pillsStyles.tabInactive,
              pressed && pillsStyles.tabPressed,
              focusedKey === tab.key && focusRingVisible && interactionStyles.focusVisible,
            ]}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            testID={testID ? `${testID}-tab-${tab.key}` : undefined}
          >
            {tab.icon && <View style={pillsStyles.iconContainer}>{tab.icon}</View>}
            <Text
              style={[
                pillsStyles.tabText,
                active ? pillsStyles.tabTextActive : pillsStyles.tabTextInactive,
              ]}
            >
              {tab.label}
            </Text>
            {tab.count !== undefined && <CountBadge count={tab.count} active={active} />}
          </Pressable>
        );
      })}
    </View>
  );
}

function SegmentTabs({
  tabs,
  activeTab,
  onChange,
  style,
  testID,
}: Readonly<Omit<TabsProps, 'variant'>>) {
  const [focusedKey, setFocusedKey] = useState<string>();
  // Only a keyboard focus draws a ring — a click should just move the pill.
  const focusRingVisible = useFocusVisible();
  const keyboard = useTabKeyboardNavigation(tabs, onChange);
  const targetSize = minimumTargetSize();
  const reducedMotion = useReducedMotion();

  /**
   * The selected pill used to be a background on whichever tab was active, so
   * changing tabs turned one background off and another on — a crossfade in
   * two places, which reads as a glitch rather than a selection moving. It is
   * now ONE indicator that travels: the tabs report their measured positions
   * and the pill animates between them.
   */
  const [layouts, setLayouts] = useState<Record<number, { x: number; width: number }>>({});
  const indicatorX = useRef(new Animated.Value(0)).current;
  const indicatorWidth = useRef(new Animated.Value(0)).current;
  const hasPositioned = useRef(false);

  const activeIndex = tabs.findIndex((tab) => tab.key === activeTab);
  const activeLayout = layouts[activeIndex];

  useEffect(() => {
    if (!activeLayout) return;

    // The first placement is where the pill already belongs, so it must not
    // fly in from x=0 on mount.
    if (!hasPositioned.current || reducedMotion) {
      hasPositioned.current = true;
      indicatorX.setValue(activeLayout.x);
      indicatorWidth.setValue(activeLayout.width);
      return;
    }

    // Width and position must move together or the pill visibly stretches
    // before it arrives. Layout properties cannot use the native driver.
    Animated.parallel([
      Animated.timing(indicatorX, {
        toValue: activeLayout.x,
        duration: motion.durations.fast,
        easing: Easing.bezier(0.16, 1, 0.3, 1),
        useNativeDriver: false,
      }),
      Animated.timing(indicatorWidth, {
        toValue: activeLayout.width,
        duration: motion.durations.fast,
        easing: Easing.bezier(0.16, 1, 0.3, 1),
        useNativeDriver: false,
      }),
    ]).start();
  }, [activeLayout, reducedMotion, indicatorX, indicatorWidth]);

  return (
    <View style={[segmentStyles.container, style]} testID={testID} accessibilityRole="tablist">
      {activeLayout && (
        <Animated.View
          testID={testID ? `${testID}-indicator` : undefined}
          pointerEvents="none"
          style={[
            segmentStyles.indicator,
            { width: indicatorWidth, transform: [{ translateX: indicatorX }] },
          ]}
        />
      )}
      {tabs.map((tab, index) => {
        const active = tab.key === activeTab;
        const isFirst = index === 0;
        const isLast = index === tabs.length - 1;

        return (
          <Pressable
            key={tab.key}
            ref={keyboard.registerTab(index)}
            onPress={() => onChange(tab.key)}
            onBlur={() => setFocusedKey(undefined)}
            onFocus={() => setFocusedKey(tab.key)}
            onLayout={(event) => {
              const { x, width } = event.nativeEvent.layout;
              setLayouts((prev) =>
                prev[index]?.x === x && prev[index]?.width === width
                  ? prev
                  : { ...prev, [index]: { x, width } },
              );
            }}
            {...(Platform.OS === 'web'
              ? ({
                  onKeyDown: (event: WebKeyEvent) => keyboard.handleKeyDown(event, index),
                  tabIndex: active ? 0 : -1,
                } as object)
              : {})}
            style={({ pressed }) => [
              segmentStyles.tab,
              { minHeight: targetSize, minWidth: targetSize },
              active ? segmentStyles.tabActive : segmentStyles.tabInactive,
              isFirst && segmentStyles.tabFirst,
              isLast && segmentStyles.tabLast,
              !isLast && segmentStyles.tabBorderRight,
              pressed && !active && segmentStyles.tabPressed,
              focusedKey === tab.key && focusRingVisible && interactionStyles.focusVisible,
            ]}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            testID={testID ? `${testID}-tab-${tab.key}` : undefined}
          >
            {tab.icon && <View style={segmentStyles.iconContainer}>{tab.icon}</View>}
            <Text
              style={[
                segmentStyles.tabText,
                active ? segmentStyles.tabTextActive : segmentStyles.tabTextInactive,
              ]}
            >
              {tab.label}
            </Text>
            {tab.count !== undefined && <CountBadge count={tab.count} active={active} />}
          </Pressable>
        );
      })}
    </View>
  );
}

export function Tabs(props: Readonly<TabsProps>) {
  switch (props.variant) {
    case 'underline':
      return <UnderlineTabs {...props} />;
    case 'pills':
      return <PillsTabs {...props} />;
    case 'segment':
      return <SegmentTabs {...props} />;
  }
}

const interactionStyles = StyleSheet.create({
  focusVisible:
    Platform.select({
      web: {
        outlineColor: colors.border.focus,
        outlineOffset: tokens.interaction.focusRing.offset,
        outlineStyle: 'solid',
        outlineWidth: tokens.interaction.focusRing.width,
      } as unknown as ViewStyle,
      default: {},
    }) ?? {},
});

const badgeStyles = StyleSheet.create({
  badge: {
    minWidth: 20,
    height: 20,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.xs,
    marginLeft: tokens.spacing.xs,
  },
  badgeText: {
    fontSize: tokens.fontSize.xs,
    fontWeight: tokens.fontWeight.semibold,
    textAlign: 'center',
  },
});

const underlineStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
  },
  tab: {
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.md,
    position: 'relative',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        userSelect: 'none',
        transitionProperty: 'opacity',
        transitionDuration: `${tokens.duration.fast}ms`,
      } as unknown as ViewStyle,
    }),
  },
  tabActive: {},
  tabPressed: {
    opacity: 0.7,
  },
  tabContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    marginRight: tokens.spacing.xs,
  },
  tabText: {
    fontSize: tokens.fontSize.base,
    fontWeight: tokens.fontWeight.medium,
  },
  tabTextActive: {
    color: colors.text.primary,
  },
  tabTextInactive: {
    color: colors.text.tertiary,
  },
  indicator: {
    position: 'absolute',
    bottom: -1,
    left: 0,
    right: 0,
    height: 2,
    borderRadius: 1,
    backgroundColor: colors.transparent,
    ...Platform.select({
      web: {
        transitionProperty: 'background-color',
        transitionDuration: `${tokens.duration.fast}ms`,
        transitionTimingFunction: tokens.easing.default,
      } as unknown as ViewStyle,
    }),
  },
  indicatorActive: {
    backgroundColor: colors.primary[500],
  },
});

const pillsStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        userSelect: 'none',
        transitionProperty: 'background-color, border-color, color',
        transitionDuration: `${tokens.duration.fast}ms`,
      } as unknown as ViewStyle,
    }),
  },
  tabActive: {
    backgroundColor: colors.primary.muted,
    borderColor: colors.primary.neon,
  },
  tabInactive: {
    backgroundColor: colors.surface.row,
    borderColor: colors.border.muted,
  },
  tabPressed: {
    backgroundColor: colors.interactive.hover,
    borderColor: colors.primary.borderActive,
  },
  iconContainer: {
    marginRight: tokens.spacing.xs,
  },
  tabText: {
    fontSize: tokens.fontSize.base,
    fontWeight: tokens.fontWeight.semibold,
  },
  tabTextActive: {
    color: colors.primary.neon,
  },
  tabTextInactive: {
    color: colors.text.primary,
  },
});

const segmentStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: colors.background.input,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.xxs,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        userSelect: 'none',
        transitionProperty: 'background-color',
        transitionDuration: `${tokens.duration.fast}ms`,
        transitionTimingFunction: tokens.easing.default,
      } as unknown as ViewStyle,
    }),
  },
  // Deliberately no background: the sliding indicator paints the pill. A
  // background here would light up the destination before the pill arrives.
  tabActive: {},
  indicator: {
    position: 'absolute',
    top: tokens.spacing.xxs,
    bottom: tokens.spacing.xxs,
    left: 0,
    backgroundColor: colors.background.card,
    borderRadius: tokens.radius.md - 2,
    ...tokens.shadow?.sm,
  },
  tabInactive: {
    backgroundColor: colors.transparent,
  },
  tabFirst: {
    borderTopLeftRadius: tokens.radius.md - 2,
    borderBottomLeftRadius: tokens.radius.md - 2,
  },
  tabLast: {
    borderTopRightRadius: tokens.radius.md - 2,
    borderBottomRightRadius: tokens.radius.md - 2,
  },
  tabBorderRight: {
    borderRightWidth: 0,
  },
  tabPressed: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  iconContainer: {
    marginRight: tokens.spacing.xs,
  },
  tabText: {
    fontSize: tokens.fontSize.sm,
    fontWeight: tokens.fontWeight.medium,
    ...Platform.select({
      web: {
        transitionProperty: 'color',
        transitionDuration: `${tokens.duration.fast}ms`,
        transitionTimingFunction: tokens.easing.default,
      } as unknown as ViewStyle,
    }),
  },
  tabTextActive: {
    color: colors.text.primary,
  },
  tabTextInactive: {
    color: colors.text.tertiary,
  },
});
